import type { LOCALES } from '@/plugins/i18n';
import { Constants } from '@/utilities/constants.ts';

const englishDomain = 'projectnewhome.com';
const frenchDomain = 'projethabitation.com';

export function getLocation() {
  if (import.meta.env.SSR) {
    return getSsrLocation();
  } else {
    return self.location;
  }
}

function getSsrLocation(): {
  hash: string;
  host: string;
  hostname: string;
  href: string;
  toString(): string;
  readonly origin: string;
  pathname: string;
  port: string;
  protocol: string;
} {
  return new URL(import.meta.env.VITE_SSR_BASE_URL);
}

export function determineLocale(): LOCALES {
  const location = getLocation();
  const hostname = location.hostname.toLowerCase();
  if (hostname.endsWith(englishDomain)) {
    return 'en-CA';
  }
  return 'fr-CA';
}

export function getAlternateBaseUrl(): string {
  const location = getLocation();
  const url = new URL(location.href);
  const hostname = url.hostname.toLowerCase();
  const hostnameParts = hostname.split('.');

  let currentDomain = '';
  let alternateDomain = '';
  if (hostname.endsWith(englishDomain)) {
    currentDomain = englishDomain;
    alternateDomain = frenchDomain;
  } else if (hostname.endsWith(frenchDomain)) {
    currentDomain = frenchDomain;
    alternateDomain = englishDomain;
  } else {
    return url.origin;
  }

  const domainParts = currentDomain.split('.');
  const subdomain = hostnameParts.slice(0, hostnameParts.length - domainParts.length).join('.');
  url.hostname = subdomain ? `${subdomain}.${alternateDomain}` : alternateDomain;
  return url.origin;
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value);
}

export function parseNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return isNumber(parsed) ? parsed : undefined;
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function parseString(value: unknown): string | undefined {
  return isString(value) ? value : undefined;
}

interface ContactResponse {
  telephone?: string;
  cellular?: string;
}

export function primaryPhoneNumber(contact: ContactResponse) {
  if (contact.telephone) {
    return contact.telephone;
  }
  if (contact.cellular) {
    return contact.cellular;
  }
  return null;
}

interface PricedModelResponse {
  price: { amount: number; type: { id: number } };
}

export function startingPrice<TModel extends PricedModelResponse>(models: TModel[]) {
  const prices = models.filter((model) => model.price.amount > 0).map((model) => model.price);
  const forSale = prices.filter((price) => price.type.id === Constants.PriceTypeId.ForSale).sort((a, b) => a.amount - b.amount);
  const forRent = prices.filter((price) => price.type.id === Constants.PriceTypeId.ForRent).sort((a, b) => a.amount - b.amount);

  if (forSale.length === 0 && forRent.length === 0) return null;
  return (forRent.length > forSale.length ? forRent[0] : forSale[0]) ?? null;
}

export function mentionsProjects(title: string): boolean {
  return /\bprojec?ts?\b/i.test(title);
}

export function getCanonicalUrl(path: string): string {
  const location = getLocation();
  const url = new URL(path, location.origin);
  return url.href;
}
